import type { TrimEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { BaseTrimMolding } from "building-types/molding/trimMoldingTypes";
import { baseTrimProfilePolyLineByName } from "building-types/profiles/defaultBoardTrimProfiles";
import type { BoardDescriptor, CustomDescriptor } from "building-types/profiles/profileTypes";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { InteriorTrimSettings } from "building-types/settings/interiorTrimSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { defaultInteriorTrimSettings } from "doc/settings/defaultSettings";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { BRep } from "geom/brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { joinPolyLine3Rails } from "geom/join";
import { line2PerpDistance } from "geom/line2";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ByTwoPoints, polyLine3Concat, polyLine3EdgeTangent, polyLine3Length } from "geom/polyLine3";
import { vec2Dot } from "geom/vec2";
import { vec3DistSquared, vec3Dot } from "geom/vec3";
import { pushMissingProfileReferenceIssue } from "../elements/profileMissingReference";
import { getActiveProfileDescriptorOrUndefined } from "../elements/profileTools";
import { baseTrimMoldingNew } from "../elements/trimMolding";
import { getWallGaps } from "../elements/walls/solidWallCrossSectionUtils";
import { getWallPath } from "../elements/walls/wall";
import { solidWallSubstrateLayer } from "../elements/walls/wallLayers";
import { WallStripImpl } from "../elements/walls/wallStripImpl";

interface MakeBaseTrimMoldingOptions {
  level: Level;
  rooms: Table<Room>;
  roomFloorAssemblies: Table<FloorAssembly>;
  walls: Table<WallSystem>;
  wallMasses: Table<WallMassSystem>;
  trimEdgeBehaviors: Table<TrimEdgeBehavior>;
  profileSource: ActiveProfileSource;
  interiorTrim?: InteriorTrimSettings;
  baseTrimMoldings: RWTable<BaseTrimMolding>;
  issues?: BuildingGenerationIssue[];
}

export function makeBaseTrimMolding({
  level,
  wallMasses,
  walls,
  trimEdgeBehaviors,
  interiorTrim = defaultInteriorTrimSettings,
  roomFloorAssemblies,
  profileSource,
  baseTrimMoldings,
  issues,
}: MakeBaseTrimMoldingOptions): Table<BaseTrimMolding> {
  const { baseBoardType, baseBoardMaterial } = interiorTrim;
  let baseTrimProfile: PolyLine2 | undefined;

  if (baseBoardType !== undefined) {
    const baseBoardDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, baseBoardType);
    if (baseBoardDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
      const { width, height } = baseBoardDescriptor as BoardDescriptor;
      baseTrimProfile = polyLine2New([0, 0, -height, 0, -height, width, 0, width, 0, 0]);
    } else if (baseBoardDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
      const { polyline } = baseBoardDescriptor as CustomDescriptor;
      baseTrimProfile = polyLine2New(polyline);
    } else if (profileSource.settingsToCatalogs) {
      pushMissingProfileReferenceIssue(
        issues,
        profileSource,
        baseBoardType,
        interiorTrim.id ? { type: "SETTINGS" as const, id: interiorTrim.id } : undefined,
        "baseBoardType",
      );
    } else {
      baseTrimProfile = baseTrimProfilePolyLineByName[baseBoardType];
    }
  }

  if (!baseTrimProfile) {
    return baseTrimMoldings;
  }

  const { id: levelId, levelBelow } = level;
  const wallIndex = walls[levelId].wallIndex;
  const roomSets = wallMasses[levelId].openConnectedRoomSets;
  const profileAabb = polyLine2AABB2(baseTrimProfile);
  const minRailThickness = aabb2Width(profileAabb) + DIST_EPSILON;
  const minRailHeightLevel = levelInchesNew(aabb2Height(profileAabb) + DIST_EPSILON);
  const roomSetProcessed: RoomSet[] = [];
  const trimEdgeBehaviorByRoomEdgeKey = new Map<string, TrimEdgeBehavior>();
  for (const trimEdgeBehavior of Object.values(trimEdgeBehaviors)) {
    trimEdgeBehaviorByRoomEdgeKey.set(`${trimEdgeBehavior.parentId}:${trimEdgeBehavior.edgeId}`, trimEdgeBehavior);
  }

  function validRailForConcat(prevRail: PolyLine3, nextRail: PolyLine3): boolean {
    const prevL = prevRail.length;
    if (
      vec3DistSquared(
        prevRail[prevL - 3],
        prevRail[prevL - 2],
        prevRail[prevL - 1],
        nextRail[0],
        nextRail[1],
        nextRail[2],
      ) > DIST_EPSILON_SQUARED ||
      Math.abs(prevRail[prevL - 1] - nextRail[2]) > DIST_EPSILON
    ) {
      return false;
    }

    const [t1X, t1Y, t1Z] = polyLine3EdgeTangent(prevRail, prevL / 2 - 2);
    const [t2X, t2Y, t2Z] = polyLine3EdgeTangent(nextRail, 0);

    // discard 180º turn
    return vec3Dot(t1X, t1Y, t1Z, t2X, t2Y, t2Z) > -ONE_MINUS_DIST_EPSILON;
  }

  type TrimRail = { currentRail: PolyLine3 | null; trimRails: PolyLine3[] };

  const addTrimRail = (
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
    currentTrimRail: TrimRail,
  ) => {
    const newRail = polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);

    if (polyLine3Length(newRail) < minRailThickness) {
      return;
    }

    if (currentTrimRail.currentRail) {
      if (validRailForConcat(currentTrimRail.currentRail, newRail)) {
        currentTrimRail.currentRail = polyLine3Concat(currentTrimRail.currentRail, newRail, false);
      } else {
        currentTrimRail.trimRails.push(currentTrimRail.currentRail);
        currentTrimRail.currentRail = null;
      }
    }

    if (!currentTrimRail.currentRail) {
      currentTrimRail.currentRail = newRail;
    }
  };

  for (const roomSet of roomSets) {
    if (roomSet.length === 0) {
      continue;
    }

    //roomSets have duplicated information for room sets that are connected to each other, so we only process the first one
    if (isRoomSetProcessed(roomSet, roomSetProcessed)) {
      continue;
    }

    const currentTrimRail: TrimRail = { currentRail: null, trimRails: [] };
    const roomSetWalls: Wall[] = [];

    // build the wall edges for this room set
    for (const room of roomSet) {
      if (!room) {
        continue;
      }
      const { roomEnclosure, openToBelow } = room.attributes;
      if (!room || !isWalledRoomEnclosure[roomEnclosure] || (openToBelow && levelBelow !== undefined)) {
        continue;
      }

      const floorAssembly = roomFloorAssemblies[room.id];
      const trimElevation = floorAssemblyTop(floorAssembly, level);
      const roomWalls = wallIndex.wallsInRoom(room);
      const wallIdToRoomEdgeId = new Map<string, string>();
      for (const wall of roomWalls) {
        if (!wallIdToRoomEdgeId.has(wall.id)) {
          const roomEdgeIndex = wallIndex.wallParentRoomEdgeIndex(room, wall);
          if (roomEdgeIndex >= 0) {
            wallIdToRoomEdgeId.set(wall.id, room.edgeIds[roomEdgeIndex]);
          }
        }
      }

      const wallStrip = new WallStripImpl(room, roomWalls, 0, roomWalls.length);
      const { pl, edgeToWallId } = wallStrip.baselineWithEdgeInfo();
      const edgeCount = polyLine2EdgeCount(pl);

      for (let i = 0; i < edgeCount; i++) {
        const wallId = edgeToWallId[i];
        const roomEdgeId = wallId ? wallIdToRoomEdgeId.get(wallId) : undefined;
        const trimEdgeBehavior = roomEdgeId ? trimEdgeBehaviorByRoomEdgeKey.get(`${room.id}:${roomEdgeId}`) : undefined;
        const hasBaseTrimMolding = trimEdgeBehavior?.hasBaseTrimMolding ?? room.attributes.hasBaseTrimMolding;
        if (!hasBaseTrimMolding) {
          if (currentTrimRail.currentRail) {
            currentTrimRail.trimRails.push(currentTrimRail.currentRail);
            currentTrimRail.currentRail = null;
          }
          continue;
        }

        const edge = polyLine2Edge(pl, i);

        if (!wallId) {
          // these are extracted later as the baselineWithEdgeInfo() fails to extract them in some situations, TODO: fix
          // addTrimRail(edge[0], edge[1], trimElevation, edge[2], edge[3], trimElevation, currentTrimRail);
          continue;
        }

        const wall = walls[levelId].walls[wallId];
        if (wall.wallType !== WallType.SOLID) {
          continue;
        }

        const { leftSide, rightSide } = wall;
        const path = getWallPath(wall);
        const { leftEdge: leftEdgeOriginal, rightEdge: rightEdgeOriginal, thickness } = solidWallSubstrateLayer(wall);
        const leftEdge = polyLine2Copy(leftEdgeOriginal); //make deep copies so we can modify them
        const rightEdge = polyLine2Copy(rightEdgeOriginal);

        let isLeftSide: boolean;
        const dx: number = edge[2] - edge[0];
        const dy: number = edge[3] - edge[1];

        const dxLeft: number = leftEdge[2] - leftEdge[0];
        const dyLeft: number = leftEdge[3] - leftEdge[1];

        if (vec2Dot(dx, dy, dxLeft, dyLeft) < 0) {
          polyLine2Reverse(leftEdge);
        }

        const dxRight: number = rightEdge[2] - rightEdge[0];
        const dyRight: number = rightEdge[3] - rightEdge[1];

        if (vec2Dot(dx, dy, dxRight, dyRight) < 0) {
          polyLine2Reverse(rightEdge);
        }

        if (
          line2PerpDistance(edge[0], edge[1], edge[2], edge[3], leftEdge[0], leftEdge[1]) < DIST_EPSILON &&
          line2PerpDistance(edge[0], edge[1], edge[2], edge[3], leftEdge[2], leftEdge[3]) < DIST_EPSILON
        ) {
          isLeftSide = false;
          if (path.leftRoom === room) {
            isLeftSide = true;
          } else if (path.rightRoom !== room) {
            continue;
          }
        } else if (
          line2PerpDistance(edge[0], edge[1], edge[2], edge[3], rightEdge[0], rightEdge[1]) < DIST_EPSILON &&
          line2PerpDistance(edge[0], edge[1], edge[2], edge[3], rightEdge[2], rightEdge[3]) < DIST_EPSILON
        ) {
          isLeftSide = true;
          if (path.rightRoom === room) {
            isLeftSide = false;
          } else if (path.leftRoom !== room) {
            continue;
          }
        } else {
          continue;
        }
        addToRoomSetWalls(wall, roomSetWalls);

        const [nX, nY] = polyLine2EdgeNormal(edge, 0);
        const halfThickness = thickness * 0.5;

        const doorGaps = getWallGaps({
          leftEdge,
          rightEdge,
          leftSide,
          rightSide,
          isLeftSide,
          includeInteriorCasing: true,
          profileSource,
          mergeGaps: true,
          includeExteriorCasing: true,
          cutPlane: minRailHeightLevel,
        });

        const useEdge = isLeftSide ? leftEdge : rightEdge;
        let prevX = useEdge[0];
        let prevY = useEdge[1];
        let prevHasCasing = true;
        for (const gap of doorGaps) {
          const { start, end, hasStartCasing, hasEndCasing } = gap;

          const [startX, startY] = polyLine2EdgeEval(useEdge, 0, start);
          const [endX, endY] = polyLine2EdgeEval(useEdge, 0, end);

          addTrimRail(prevX, prevY, trimElevation, startX, startY, trimElevation, currentTrimRail);

          if (!hasStartCasing) {
            addTrimRail(
              startX,
              startY,
              trimElevation,
              startX + nX * halfThickness,
              startY + nY * halfThickness,
              trimElevation,
              currentTrimRail,
            );
          }
          if (!hasEndCasing) {
            addTrimRail(
              endX + nX * halfThickness,
              endY + nY * halfThickness,
              trimElevation,
              endX,
              endY,
              trimElevation,
              currentTrimRail,
            );
          }

          prevX = endX;
          prevY = endY;
          prevHasCasing = hasEndCasing;
        }

        if (!prevHasCasing) {
          addTrimRail(prevX, prevY, trimElevation, prevX, prevY, trimElevation, currentTrimRail);
        }

        addTrimRail(prevX, prevY, trimElevation, useEdge[2], useEdge[3], trimElevation, currentTrimRail);
      }
    }
    //@TODO -- add trims around the open wall ends
    for (const wall of roomSetWalls) {
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }
      const { start, end, floorElevation } = solidWallSubstrateLayer(wall);
      const {
        isFreeEnd: isFreeEndStart,
        verts: vertsStart,
        offsetDifferenceOutline: offsetDifferenceOutlineStart,
      } = start;
      if (isFreeEndStart && vertsStart) {
        addTrimRail(
          vertsStart[0],
          vertsStart[1],
          floorElevation,
          vertsStart[2],
          vertsStart[3],
          floorElevation,
          currentTrimRail,
        );
      }
      offsetDifferenceOutlineStart
        ? addTrimRail(
            offsetDifferenceOutlineStart[0],
            offsetDifferenceOutlineStart[1],
            floorElevation,
            offsetDifferenceOutlineStart[2],
            offsetDifferenceOutlineStart[3],
            floorElevation,
            currentTrimRail,
          )
        : null;

      const { isFreeEnd: isFreeEndEnd, verts: vertsEnd, offsetDifferenceOutline: offsetDifferenceOutlineEnd } = end;
      if (isFreeEndEnd && vertsEnd) {
        addTrimRail(
          vertsEnd[0],
          vertsEnd[1],
          floorElevation,
          vertsEnd[2],
          vertsEnd[3],
          floorElevation,
          currentTrimRail,
        );
      }
      offsetDifferenceOutlineEnd
        ? addTrimRail(
            offsetDifferenceOutlineEnd[0],
            offsetDifferenceOutlineEnd[1],
            floorElevation,
            offsetDifferenceOutlineEnd[2],
            offsetDifferenceOutlineEnd[3],
            floorElevation,
            currentTrimRail,
          )
        : null;
    }
    //TODO -- add trim rails for the open door ends, this will require filtering through the doors and finding the ones that are open

    if (currentTrimRail.currentRail) {
      if (
        currentTrimRail.trimRails.length > 0 &&
        validRailForConcat(currentTrimRail.currentRail, currentTrimRail.trimRails[0])
      ) {
        currentTrimRail.trimRails[0] = polyLine3Concat(
          currentTrimRail.currentRail,
          currentTrimRail.trimRails[0],
          false,
        );
      } else {
        currentTrimRail.trimRails.push(currentTrimRail.currentRail);
      }
    }

    if (currentTrimRail.trimRails.length !== 0) {
      const trims: BRep[] = [];
      const railConnected = joinPolyLine3Rails({
        segments: currentTrimRail.trimRails,
        searchBoxTolerance: DIST_EPSILON,
        simplify: true,
      });

      for (let r = 0, lr = railConnected.length; r < lr; r++) {
        trims.push(extrudeFaceAlongPolyLine3([baseTrimProfile], railConnected[r], 0, 0, 1));
      }

      const finalTrim = baseTrimMoldingNew(levelId, roomSet, trims, baseBoardMaterial, railConnected);
      baseTrimMoldings[finalTrim.id] = finalTrim;
    }
  }

  return baseTrimMoldings;
}

export function isRoomSetProcessed(roomSet: RoomSet, roomSetProcessed: RoomSet[]): boolean {
  const roomSetSorted = [...roomSet];
  roomSetSorted.sort((a, b) => (a.id > b.id ? 1 : -1));
  for (let i = 0; i < roomSetProcessed.length; i++) {
    if (
      roomSetSorted.length === roomSetProcessed[i].length &&
      roomSetProcessed[i].every((element, index) => {
        return element.id === roomSetSorted[index].id;
      })
    ) {
      return true;
    }
  }
  roomSetProcessed.push(roomSetSorted);
  return false;
}

function addToRoomSetWalls(wall: Wall, roomSetWalls: Wall[]) {
  if (
    !roomSetWalls.some((w) => {
      return w.id === wall.id;
    })
  ) {
    roomSetWalls.push(wall);
  }
}
